import { getChangelogSidebarChildren } from '@/components/painel/changelog/changelog-nav-config';
import { AceNavIcon } from '@/components/help-center/aces/AceIcon';
import {
  EquipeNavIcon,
  PawnNavIcon,
  PowerNavIcon,
} from '@/components/help-center/MaskedSvgIcon';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Tags,
  BookOpen,
  FileText,
  Users,
  Shield,
  Settings,
  BarChart3,
  Activity,
  Search,
  BookMarked,
  Zap,
  ClipboardCheck,
  Type,
  Construction,
  Database,
  SearchCode,
  Library,
  Scale,
  Fingerprint,
} from 'lucide-react';

export type PainelNavKey =
  | 'dashboard'
  | 'categorias'
  | 'guias'
  | 'powers'
  | 'aces'
  | 'paginas'
  | 'homepage'
  | 'equipe'
  | 'regras'
  | 'conduta'
  | 'usuarios'
  | 'permissoes'
  | 'analytics_visao'
  | 'analytics_trafego'
  | 'analytics_pesquisas'
  | 'analytics_guias'
  | 'analytics_powers'
  | 'analytics_cms'
  | 'analytics_usuarios'
  | 'changelog_overview'
  | 'changelog_versions'
  | 'changelog_categories'
  | 'revisao'
  | 'textos_painel'
  | 'identidade'
  | 'coming_soon'
  | 'seo'
  | 'icon_library'
  | 'configuracoes';

export type PainelNavItem = {
  key: PainelNavKey;
  label: string;
  href: string;
  icon: LucideIcon;
  /** Se true, só fica ativo com href exato (ex.: overview de Analytics). */
  matchExact?: boolean;
};

export type PainelNavGroup = {
  id: 'content' | 'management' | 'analytics' | 'changelog' | 'system';
  label: string;
  items: PainelNavItem[];
};

const PAINEL_CHANGELOG_ITEMS: PainelNavItem[] = getChangelogSidebarChildren('xat').map((c) => ({
  key: c.key as PainelNavKey,
  label: c.label,
  href: c.href,
  icon: c.icon,
}));

export const PAINEL_NAV_GROUPS: PainelNavGroup[] = [
  {
    id: 'content',
    label: 'CONTENT',
    items: [
      { key: 'dashboard', label: 'Dashboard', href: '/painel', icon: LayoutDashboard },
      { key: 'categorias', label: 'Categorias', href: '/painel/categorias', icon: Tags },
      { key: 'guias', label: 'Guias', href: '/painel/guias', icon: BookOpen },
      { key: 'revisao', label: 'Revisão', href: '/painel/revisao', icon: ClipboardCheck },
      { key: 'powers', label: 'Powers', href: '/painel/powers', icon: PowerNavIcon },
      { key: 'aces', label: 'Aces', href: '/painel/aces', icon: AceNavIcon },
    ],
  },
  {
    id: 'management',
    label: 'MANAGEMENT',
    items: [
      { key: 'equipe', label: 'Equipe', href: '/painel/equipe', icon: EquipeNavIcon },
      { key: 'regras', label: 'Regras', href: '/painel/paginas/regras', icon: BookOpen },
      { key: 'conduta', label: 'Código de Conduta', href: '/painel/paginas/conduta', icon: Scale },
      { key: 'usuarios', label: 'Usuários', href: '/painel/usuarios', icon: PawnNavIcon },
      { key: 'permissoes', label: 'Permissões', href: '/painel/permissoes', icon: Shield },
    ],
  },
  {
    id: 'analytics',
    label: 'ANALYTICS',
    items: [
      {
        key: 'analytics_visao',
        label: 'Visão Geral',
        href: '/painel/analytics',
        icon: BarChart3,
        matchExact: true,
      },
      { key: 'analytics_trafego', label: 'Tráfego', href: '/painel/analytics/trafego', icon: Activity },
      { key: 'analytics_pesquisas', label: 'Pesquisas', href: '/painel/analytics/pesquisas', icon: Search },
      { key: 'analytics_guias', label: 'Guias', href: '/painel/analytics/guias', icon: BookMarked },
      { key: 'analytics_powers', label: 'Powers', href: '/painel/analytics/powers', icon: Zap },
      { key: 'analytics_cms', label: 'CMS', href: '/painel/analytics/cms', icon: Database },
      { key: 'analytics_usuarios', label: 'Usuários', href: '/painel/analytics/usuarios', icon: Users },
    ],
  },
  {
    id: 'changelog',
    label: 'CHANGELOGS',
    items: PAINEL_CHANGELOG_ITEMS,
  },
  {
    id: 'system',
    label: 'SYSTEM',
    items: [
      { key: 'paginas', label: 'Páginas', href: '/painel/paginas', icon: FileText },
      { key: 'textos_painel', label: 'Textos do painel', href: '/painel/textos', icon: Type },
      { key: 'identidade', label: 'Identidade do site', href: '/painel/identidade', icon: Fingerprint },
      { key: 'coming_soon', label: 'Coming Soon', href: '/painel/coming-soon', icon: Construction },
      { key: 'seo', label: 'SEO', href: '/painel/seo', icon: SearchCode },
      { key: 'icon_library', label: 'Icon Library', href: '/painel/icon-library', icon: Library },
      { key: 'configuracoes', label: 'Minha conta', href: '/painel/configuracoes', icon: Settings },
    ],
  },
];

export const PAINEL_NAV_FLAT: PainelNavItem[] = PAINEL_NAV_GROUPS.flatMap((g) => g.items);

/** @deprecated Preferir `PAINEL_NAV_GROUPS` ou `PAINEL_NAV_FLAT`. */
export const PAINEL_NAV = PAINEL_NAV_FLAT;

export function painelLabelForSlug(slug: string): string {
  const item = PAINEL_NAV_FLAT.find((n) => n.href === `/painel/${slug}`);
  if (item) return item.label;
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Título de navegação mais específico para o caminho atual (ex.: sub-rotas de guias). */
export function painelNavTitleForPathname(pathname: string): string | undefined {
  const normalized = pathname.replace(/\/$/, '') || '/painel';
  if (normalized === '/painel/changelog') return 'Overview';
  const productOverview = normalized.match(/^\/painel\/changelog\/(xat|ajuda)(?:\/overview)?$/);
  if (productOverview) {
    return productOverview[1] === 'ajuda' ? 'Changelog xat Ajuda' : 'Changelog xat';
  }
  const versionDetail = normalized.match(/^\/painel\/changelog\/(?:xat|ajuda)\/versions\/([^/]+)$/);
  if (!versionDetail) {
    const legacyVersionDetail = normalized.match(/^\/painel\/changelog\/versions\/([^/]+)$/);
    if (legacyVersionDetail && legacyVersionDetail[1] !== 'novo') {
      try {
        return `v${decodeURIComponent(legacyVersionDetail[1])}`;
      } catch {
        return `v${legacyVersionDetail[1]}`;
      }
    }
  }
  if (versionDetail && versionDetail[1] !== 'novo') {
    try {
      return `v${decodeURIComponent(versionDetail[1])}`;
    } catch {
      return `v${versionDetail[1]}`;
    }
  }
  if (/\/painel\/changelog\/(?:xat|ajuda)\/versions\/[^/]+\/edit/.test(normalized)) return 'Editor de changelog';
  if (/\/painel\/changelog\/versions\/[^/]+\/edit/.test(normalized)) return 'Editor de changelog';
  if (normalized === '/painel/changelog/xat/versions/novo' || normalized === '/painel/changelog/ajuda/versions/novo') {
    return 'Nova versão';
  }
  if (normalized === '/painel/changelog/versions/novo') return 'Nova versão';
  if (normalized === '/painel/powers/new') return 'Novo power';
  if (/\/painel\/powers\/[^/]+\/edit/.test(normalized)) return 'Editar power';
  const candidates = PAINEL_NAV_FLAT.filter((item) => {
    if (item.href === '/painel') return normalized === '/painel';
    if (item.matchExact) return normalized === item.href;
    return normalized === item.href || normalized.startsWith(`${item.href}/`);
  });
  if (candidates.length === 0) return undefined;
  candidates.sort((a, b) => b.href.length - a.href.length);
  return candidates[0].label;
}
